// @flow

import classNames from 'classnames';
import React, { useState, useRef } from 'react';
import type { Node } from 'react';
import { Button, ClickOutsideListener } from '@riseart/common';

import {
  menuSwitchCls,
  menuSwitchAnimatedCls,
  menuSwitchOpenedCls,
  menuSwitchLabelCls,
  menuSwitchLabelStaticCls,
  menuSwitchLabelValueCls,
  menuSwitchDrawerCls,
  menuSwitchListCls,
  menuSwitchSelectedCls,
} from 'shared_components/common/menu/switch/Switch.less';

type Props = {
  animated?: boolean,
  label: Node,
  selected?: number,
  options: Array<{ label: Node, value: number }>,
  onClick?: Function,
  href?: string,
};

/**
 * MenuSwitch
 *
 * @param {Props} props
 */
export const MenuSwitch = ({
  animated = false,
  label,
  selected = null,
  options,
  onClick = null,
  href = null,
}: Props): Node => {
  const containerRef = useRef(null);
  const [isOpened, setIsOpened] = useState(!animated);
  const selectedValueLabel =
    animated &&
    selected !== null &&
    options.reduce(
      (accumulator, { label, value }) => (value === selected ? label : accumulator),
      '',
    );

  const handleClick = (e: Event) => {
    e.preventDefault();
    setIsOpened(!isOpened);
  };

  const handleOptionClick = (value) => (e: Event) => {
    e.preventDefault();
    setIsOpened(false);

    if (typeof onClick === 'function') {
      onClick(value);
    }
  };

  /**
   * Menu click outside feature
   */
  const handleClickOutside = (e: Object) => {
    const isClickedOutside =
      containerRef && containerRef.current && !containerRef.current.contains(e.target);

    if (isClickedOutside) {
      setIsOpened(false);
    }
  };

  const optionsList = options.map(({ label, value }) => (
    <li key={value}>
      <Button
        {...(selected === value ? { className: menuSwitchSelectedCls } : null)}
        {...(href ? { isAnchor: true, href } : { onClick: handleOptionClick(value) })}
      >
        {label}
      </Button>
    </li>
  ));

  return (
    <div
      ref={containerRef}
      className={classNames(
        animated ? menuSwitchAnimatedCls : menuSwitchCls,
        isOpened && menuSwitchOpenedCls,
      )}
    >
      {animated && isOpened ? <ClickOutsideListener onClickOutside={handleClickOutside} /> : null}
      <div className={menuSwitchLabelCls} onClick={handleClick}>
        <span className={menuSwitchLabelStaticCls}>{label}</span>
        {selectedValueLabel ? (
          <span className={menuSwitchLabelValueCls}>{selectedValueLabel}</span>
        ) : null}
      </div>
      <div className={menuSwitchDrawerCls}>
        <ul className={menuSwitchListCls}>{optionsList}</ul>
      </div>
    </div>
  );
};
